/*
 * flashmeeting-fe
 *
 * Author: Andgel BRISSAUD
 * Copyright (c) 2024 Prodeo - All Rights Reserved
 */

/* ===== External Libraries ===== */
import { AXIOS, CallbackErrorAxios, CallbackWarningAxios, CommonGlobalService, CommonUtilsService, UUID } from '@umanforge/common';
import { AxiosResponse, AxiosError } from 'axios';

/* ===== API Configuration ===== */
import { API } from 'constants/api.constant';
import { Dispatch } from '@reduxjs/toolkit';
import { GlobalService } from './global.service';
import { ApiTheme, Theme } from '../interfaces/theme.interface';

/* ===== Interfaces ===== */
import { CreateThemeRequest } from '../interfaces/theme.interface';

/* ===== Class ===== */
export class ThemeService {
  static canDeleteThemeOnEventManager = (
    themeId: UUID,
    eventManagerId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> => {
    return new Promise( async resolve => {
      AXIOS.get( `/api/fm/themes/${themeId}/can-delete/${eventManagerId}`,
        await CommonGlobalService.getHeaderRequest( token )
      )
        .then( ( response: AxiosResponse<{ canDelete: boolean }> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data.canDelete );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  };

  static updateTheme = (
    theme: Theme,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Theme> => {
    const body = { name: theme.name, color: theme.color, eventManagerIds: theme.eventManagerIds, siteIds: theme.siteIds };
    return new Promise( async resolve => {
      AXIOS.put( `${API.THEMES}/${theme.id}`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<Theme> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  };


  static getThemesBySites = (
    siteIds: UUID[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Theme[]> => {
    return new Promise( async resolve => {
      AXIOS.post( `${API.THEMES_BY_SITES}`, { siteIds }, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<Theme[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          const dataSorted = CommonUtilsService.sortByKey( response.data, 'name', 'asc' );
          resolve( dataSorted );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  };

  static getCommonThemesByEventManager = (
    eventManagerIds: UUID[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Theme[]> => {
    return new Promise( async resolve => {
      AXIOS.post( `${API.COMMON_THEMES_BY_EVENT_MANAGER}`, { eventManagerIds }, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<Theme[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  };


  static getThemesByEventManager = (
    eventManagerIds: UUID[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<ApiTheme[]> => {
    return new Promise( async resolve => {
      AXIOS.post( `${API.THEMES_BY_EVENT_MANAGER}`, { eventManagerIds }, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<ApiTheme[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  };


  static getThemeById = (
    themeId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Theme> => {
    return new Promise( async resolve => {
      AXIOS.get( `${API.THEMES}/${themeId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<Theme> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  };


  static deleteTheme = (
    themeId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<void> => {
    return new Promise( async resolve => {
      AXIOS.delete( `${API.THEMES}/${themeId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve();
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  };

  static createTheme = (
    themeData: CreateThemeRequest,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Theme> => {
    return new Promise( async resolve => {
      AXIOS.post( `${API.THEMES}`, themeData, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<Theme> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  };

  static getThemesByConnectedUser = (
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Theme[]> => {
    return new Promise( async resolve => {
      AXIOS.get( `${API.THEMES}/user/get-all-themes`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<Theme[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  };

  static getThemesForExtractionFilters = (
    params: {
      clientIds?: UUID[];
      siteIds?: UUID[];
      sectorIds?: UUID[];
      eventManagerIds?: UUID[];
    },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Theme[]> => {
    return new Promise( async resolve => {
      AXIOS.post( `${API.THEMES_EXTRACTION_BY_FILTERS}`, params, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<Theme[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  };

  static deleteSiteOnTheme = (
    themeId: UUID, siteId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> => {
    return new Promise( async resolve => {
      AXIOS.delete( `${API.THEMES}/${siteId}/sites`, { ...await CommonGlobalService.getHeaderRequest( token ), params: { themeId } } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  };

  static deleteEventManagerOnTheme = (
    themeId: UUID, eentManagerId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> => {
    return new Promise( async resolve => {
      AXIOS.delete( `${API.THEMES}/${eentManagerId}/event-managers`, { ...await CommonGlobalService.getHeaderRequest( token ), params: { themeId } } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  };

  static mergeThemes = (
    themeIds: UUID[],
    name: string,
    color: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Theme> => {
    const body = { themeIds, name, color };
    return new Promise( async resolve => {
      AXIOS.post( `${API.THEMES}/merge/themes-by-ids`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<Theme> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  };

}
